<template>
    <div :id="rootElementId" :style="`height:${SVG_HEIGHT}px`" ref="rootElementRef"></div>
</template>

<script>
import { getDataUrl, getNeutralColors } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import * as d3v4 from "d3v4";

  export default {
    name: 'RaceChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      game:{
        type:Object,
        default: () => {}
      },
      shotData:{
        type:Array,
        default: () => {}
      },
      isForPDF: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        images: {}
      }
    },
    watch: {
      shotData: { handler() { this.renderD3(); }, deep: true }
    },
    computed: {
      SVG_WIDTH(){
        if(this.size){
          return this.size
        }else if(this.$refs.rootElementRef){
          return this.$refs.rootElementRef.getBoundingClientRect().width;
        }else if(document.getElementById(this.rootElementId) && document.getElementById(this.rootElementId).getBoundingClientRect()){
          return document.getElementById(this.rootElementId).getBoundingClientRect().width;
        }else{
          return 420;
        }
      },
      SVG_HEIGHT(){
        return this.SVG_WIDTH * 0.67125;
      },
      PITCH_WIDTH(){
        if(this.CHART_MARGIN){
          return this.SVG_WIDTH - this.CHART_MARGIN.left - this.CHART_MARGIN.right
        }else{
          return this.SVG_WIDTH;
        }
      },
      PITCH_HEIGHT() {
        if(this.CHART_MARGIN){
          return this.SVG_HEIGHT - this.CHART_MARGIN.top - this.CHART_MARGIN.bottom
        }else{
          return this.SVG_HEIGHT;
        }
      },
      CHART_SIZE_RATIO(){
        const ratio = this.SVG_WIDTH <= 380 ? 0.002 : this.SVG_WIDTH < 700 ? 0.0017 : 0.00135;
        return (this.SVG_WIDTH / 2) * ratio;
      },
      rootElementId(){
        return `raceChart${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      },
      CHART_MARGIN(){
        return this.margin ? this.margin : {top: this.SVG_WIDTH * 0.0975, right: this.SVG_WIDTH * 0.18, bottom: this.SVG_WIDTH * 0.1, left: this.SVG_WIDTH * 0.1};
      },
      darkMode(){
        return this.$vuetify.theme.dark;
      },
      shotDataHome(){
        return this.shotData.filter(d => (d.teamId === this.game.homeTeamId && !d.ownGoal) || (d.teamId === this.game.awayTeamId && d.ownGoal))
      },
      shotDataAway(){
        return this.shotData.filter(d => (d.teamId === this.game.awayTeamId && !d.ownGoal) || (d.teamId === this.game.homeTeamId && d.ownGoal))
      },
      lastMinute(){
        return _.max([d3.max(this.shotData,(d,i) => d.min),d3.max(this.shotData,(d,i) => d.periodId) <= 4 ? 90 : 120])
      }
      
    },
    mounted() {
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
      this.renderD3();
    },
    methods: {
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      },
      async renderD3(){
        d3v4.select(`#${this.rootElementId} > svg`).remove();
        d3.selectAll('.tooltip').remove();

        const tooltip = d3v4
          .select("body")
          .append("div")
          .attr("class", "tooltip no-arrow");

        var svg = d3v4.select(`#${this.rootElementId}`)
          .append("svg")
          .attr("width", this.SVG_WIDTH)
          .attr("height", this.SVG_HEIGHT)
          .style("border-radius", '5px')
          .attr("class", "elevation-3")
          .attr("id", `${this.rootElementId}SVG`)
          .style("font-family","NeueMontreal")

        this.renderChart(svg)

        this.renderTitle(svg);

        this.renderLogo(svg);

        let font = await loadFonts();
        copyAndAppendElement(svg.node(),font.node(),0,0);

        //Emit when is loaded
        this.$emit("loaded", this.id);
      },
      renderChart(svg){
        
        let chartContainer = svg
          .append("g")
          .attr("transform", `translate(${this.CHART_MARGIN.left},${this.CHART_MARGIN.top})`)
          .style("font-family","NeueMontreal")

        //AxisLeft
        let homeXgSum = d3.sum(this.shotDataHome, (d) => d.xG);
        let awayXgSum = d3.sum(this.shotDataAway, (d) => d.xG);
        let yDomain = [
          0,
          Math.max(1, Math.ceil(Math.max(homeXgSum, awayXgSum)))
        ];
        let yRange = yDomain[1] - yDomain[0];
        let axisPaddingY = 0.125
        let y = d3.scale.linear().range([ this.PITCH_HEIGHT,0 ])
        let yAxis = d3.svg.axis().scale(y)
                  .orient("left")
                  .ticks(6)
                  .tickSize(-this.PITCH_WIDTH, 0, 0)
                  .tickPadding(8)
                  .tickFormat(d => d);
          y.domain([yDomain[0] - (yRange * axisPaddingY),yDomain[1] + (yRange * axisPaddingY)])

          chartContainer.append("g")
          .attr("class", "y axis-race-chart")
          .call(yAxis)
          .append("text")
          .attr("transform", "rotate(-90)")
          .attr("x", (-this.PITCH_HEIGHT/2))
          .attr("y", -this.CHART_MARGIN.left/1.3)
          .attr("dy", ".71em")
          .style("fill", this.isForPDF ? "#000" : "")
          .style("text-anchor", "middle")
          .style("font-weight","bold")
          .style("font-size",this.SVG_WIDTH * 0.018)
          .text(this.$t("finishingMap.chart.sumOfxG"));

          chartContainer.append("g")
          .attr("class", "x axis-race-chart")
          .append("text")
          .attr("x", (this.PITCH_WIDTH/2) - 5)
          .attr("y", this.PITCH_HEIGHT + this.CHART_MARGIN.top/2)
          .attr("dy", ".71em")
          .style("fill", this.isForPDF ? "#000" : "")
          .style("text-anchor", "middle")
          .style("font-weight","bold")
          .style("font-size",this.SVG_WIDTH * 0.018)
          .text("Min'");

        let allPeriods = _.chain(this.shotData).map(sd => sd.periodId).some(p => p === 3 || p === 4).value() ? [1,2,3,4] : [1,2]

        _.forEach(allPeriods,(p,i) => {
          let start,end,ticks;
          switch(i){
            case 0:
              start = 0;
              end = this.PITCH_WIDTH * (_.size(allPeriods) > 2 ? 0.33 : 0.5);
              ticks = _.size(allPeriods) > 2 ? 3 : 5;
              break;
            case 1:
              start = this.PITCH_WIDTH * (_.size(allPeriods) > 2 ? 0.33 : 0.5);
              end = this.PITCH_WIDTH * (_.size(allPeriods) > 2 ? 0.66 : 1);
              ticks = _.size(allPeriods) > 2 ? 3 : 5;
              break;
            case 2:
              start = this.PITCH_WIDTH * 0.66
              end = this.PITCH_WIDTH * 0.83
              ticks = 2;
              break;
            case 3:
              start = this.PITCH_WIDTH * 0.83;
              end = this.PITCH_WIDTH;
              ticks = 2;
              break;
            default:
              start = 0;
              end = this.PITCH_WIDTH;
              ticks = 10;
              break;
          }
          this.drawPeriod(p,chartContainer,y,start,end,ticks)
        })
        let baseYHome = y(d3.sum(this.shotDataHome,(sd,i) => sd.xG));
        let baseYAway = y(d3.sum(this.shotDataAway,(sd,i) => sd.xG));
        let labelsTooClose = baseYAway !== 0 && baseYHome / baseYAway > 0.8 && baseYHome / baseYAway < 1.75;
        this.drawLegend(this.shotDataHome,chartContainer,null,y,getNeutralColors('home')[0],this.game.homeTeamName, labelsTooClose ? (baseYHome > baseYAway ? 1.25 : 0.8) : 1);
        this.drawLegend(this.shotDataAway,chartContainer,null,y,getNeutralColors('away')[0],this.game.awayTeamName, labelsTooClose ? (baseYHome > baseYAway ? 0.8 : 1.25) : 1); 
      },
      drawPeriod(period,chartContainer,y,start,end,ticks){
        let periodShots = _.chain(this.shotData).filter(sd => sd.periodId === period).value();
        //AxisBottom
        let xDomain = [_.min([this.getPeriodRange(period)[0],_.chain(periodShots).minBy('min').get('min').value()]),
          _.max([this.getPeriodRange(period)[1],_.chain(periodShots).maxBy('min').get('min').value()])
        ]
        let xRange = xDomain[1] - xDomain[0];
        let axisPaddingX = 0.033
        let x = d3.scale.linear().range([start, end])
        let xAxis = d3.svg.axis().scale(x)
                    .orient("bottom")
                    .ticks(ticks)
                    .tickSize(-this.PITCH_HEIGHT, 0, 0)
                    .tickFormat(d => d)
          x.domain([xDomain[0] - (xRange * axisPaddingX),xDomain[1] + (xRange * axisPaddingX)])

        chartContainer.append("line")
          .attr("x1",x(xDomain[1])).attr("y1",this.CHART_MARGIN.top * 0.66)
          .attr("x2",x(xDomain[1])).attr("y2",this.PITCH_HEIGHT)
          .style("stroke","darkgray")
          .style("stroke-dasharray","3 2")
          .style("stroke-width",1)

        chartContainer.append("text")
          .attr("x", start + ((end - start) / 2)).attr("y",this.CHART_MARGIN.top * 0.33)
          .style("fill", this.isForPDF ? "#000" : "")
          .style("text-anchor","middle")
          .style("font-size",this.SVG_WIDTH * 0.018)
          .style("font-weight","bold")
          .text(this.periodToLabel(period))

        chartContainer.append("g")
          .attr("class", "x axis-race-chart")
          .attr("transform", `translate(0,${this.PITCH_HEIGHT})`)
          .call(xAxis);

        let homeShotsPeriod = _.chain(this.shotDataHome).filter(s => s.periodId === period).value();
        let xgAccumHome = _.chain(this.shotDataHome).filter(s => s.periodId < period).reduce((accum,s) => accum += s.xG,0).value();
        this.drawLineAccum(homeShotsPeriod,chartContainer,x,y,getNeutralColors('home')[0],xDomain,xgAccumHome);


        let awayShotsPeriod = _.chain(this.shotDataAway).filter(s => s.periodId === period).value();
        let xgAccumAway = _.chain(this.shotDataAway).filter(s => s.periodId < period).reduce((accum,s) => accum += s.xG,0).value();
        this.drawLineAccum(awayShotsPeriod,chartContainer,x,y,getNeutralColors('away')[0],xDomain,xgAccumAway);
      },
      drawLineAccum(data,svg,x,y,color,xDomain,xgAccum){
        let line = "M",
            fill = `M0,${this.PITCH_HEIGHT}`;


        data = [{xG:xgAccum ,min:xDomain[0]},...data]

        if(_.size(data) === 2){
          data = [...data,{xG:0,min:xDomain[1]}]
        }else{
          data = [...data,{xG:_.last(data).xG,min:xDomain[1]}]
        }
        
        data.forEach((d, i) => {
          let y0 = y(d3.sum(data.filter((d,j) => j <= i),(d) => d.xG)),
              x0 = x(d.min);
            
          if (i === 0) {
            let offset = d.min >= 45 ? 3 : 0;
            line += `${x(d.min-offset)},${y0}H${x0}`;
          } else {
            line += `H${x0}`;
          }

          fill += `V${y0}H${x0}`;

          if (data[i + 1] && data[i].min != 0) {
            line += `V${y(d3.sum(data.filter((d,j) => j < i + 1),(d) => d.xG))}`;
          }

          if(d.type === "GOAL"){
            svg.append("circle")
              .attr("cx", x(d.min))
              .attr("cy", data[i + 1] ? y(d3.sum(data.filter((d,j) => j < i + 1),(d) => d.xG)) : y0)
              .attr("r", this.PITCH_WIDTH * 0.015)
              .attr("id",`circle-goal-${d.id}`)
              .style("fill",color)
              .style("stroke",color)
              .style("stroke-width",1.5)
              .style("stroke-opacity",0.9)

            svg.append("text")
              .attr("x", x(d.min))
              .attr("y", data[i + 1] ? y(d3.sum(data.filter((d,j) => j < i + 1),(d) => d.xG)) : y0)
              .attr("dy", ".35em")
              .style("cursor","default")
              .style("text-anchor", "middle")
              .style("font-size",this.SVG_WIDTH * 0.012)
              .style("font-weight","bold")
              .style("fill","white")
              .text("G")
              .on('mouseenter',() => {
                let tooltipText= `
                  <div style="display:flex;flex-direction:column;align-items:center;padding:5px; 15px;">
                    <span style="font-weight:bold;">${d.player}</span>
                    <span style="font-weight:bold;">
                      ${
                        _.filter(this.shotData,(sd) => sd.min <= d.min && sd.periodId <= d.periodId && sd.teamId === this.game.homeTeamId && sd.type === 'GOAL' && sd.ownGoal === false).length +
                        _.filter(this.shotData,(sd) => sd.min <= d.min && sd.periodId <= d.periodId && sd.teamId === this.game.awayTeamId && sd.type === 'GOAL' && sd.ownGoal === true).length
                      }
                      -
                      ${
                        _.filter(this.shotData,(sd) => sd.min <= d.min && sd.periodId <= d.periodId && sd.teamId === this.game.awayTeamId && sd.type === 'GOAL' && sd.ownGoal === false).length +
                        _.filter(this.shotData,(sd) => sd.min <= d.min && sd.periodId <= d.periodId && sd.teamId === this.game.homeTeamId && sd.type === 'GOAL' && sd.ownGoal === true).length
                      }
                    </span>
                  </div>
                `
                let circle = d3v4.select(`#circle-goal-${d.id}`).node();
                d3v4.select("body > .tooltip")
                  .style("top", circle.getBoundingClientRect().top + window.scrollY + (this.PITCH_WIDTH * 0.015) + 15 + "px")
                  .style("height","auto")
                  .style("width","auto")
                  .style("display", "inline-block")
                  .style("left", () => {
                    return `${circle.getBoundingClientRect().left  - (d3v4.select("body > .tooltip").node().getBoundingClientRect().width / 2)}px`
                  })
                  .style("font-size", this.SVG_WIDTH * 0.017)
                  .html(tooltipText);
              })
            .on('mouseout',(d) => {
              d3.select("body > .tooltip").style("display", "none");
            })
          }
        });

        fill += `V${this.PITCH_HEIGHT}Z`;

        svg.insert("path","circle")
          .attr("class", "line")
          .style("fill","none")
          .style("stroke-width","2.5px")
          .style("stroke",color)
          .attr("d", line);

        svg.insert("path","circle")
          .attr("class", "path-fill")
          .style("fill","transparent")
          .attr("d", fill);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      drawLegend(data,svg,x,y,color,title, corrector = 1){
        let yOffset = 35 * this.CHART_SIZE_RATIO;
        let baseY = y(d3.sum(data,(sd,i) => sd.xG));
        let titleText = svg.append('text')
          .attr("y", baseY * corrector - yOffset)
          .attr("x", this.PITCH_WIDTH + (this.CHART_MARGIN.right/2))
          .attr("dy", ".31em")
          .style("text-anchor", "middle")
          .style("font-weight","bold")
          .style("font-size",this.SVG_WIDTH * 0.022)
          .style("fill",color)
          .text(`${title}`);

        if(titleText.node().getComputedTextLength() > this.CHART_MARGIN.right){
          titleText.text(title.split(" ")[0])
        }

        svg.append('text')
          .attr("y", baseY * corrector)
          .attr("x", this.PITCH_WIDTH + (this.CHART_MARGIN.right/2))
          .attr("dy", ".31em")
          .style("text-anchor", "middle")
          .style("font-weight","bold")
          .style("font-size", this.SVG_WIDTH * 0.020)
          .style("fill",color)
          .text(`${_.filter(data,(d) => d.type === 'GOAL').length} ${this.$t("playerScout.goals")} (${d3.sum(data,(d) => d.xG).toFixed(1)} ${this.$t("playerScout.xG")})`);
      },
      getPeriodRange(period){
        switch(period){
          case 1: return [0,45]
          case 2: return [45,90]
          case 3: return [90,105]
          case 4: return [105,120]
        }
        return [0,90];
      },
      periodToLabel(period){
        switch(period){
          case 1: return this.$t("general.firstHalf");
          case 2: return this.$t("general.secondHalf");
          case 3: return this.$t("general.firstPeriodOT");
          case 4: return this.$t("general.secondPeriodOT");
        }
        return "";
      },
      renderTitle(svg){
        //Title
        svg
          .append("text")
          .attr("x", this.CHART_MARGIN.left * 0.66)
          .attr("y", this.CHART_MARGIN.top * 0.5)
          .attr("text-anchor","start")
          .style("font-size",this.SVG_WIDTH * 0.025)
          .style("font-weight",600)
          .style("fill", this.darkMode && !this.isForPDF ? "white" : "rgba(0,0,0,0.69)")
          .text(`${this.game.homeTeamName} ${this.game.homeTeamScore} - ${this.game.awayTeamScore} ${this.game.awayTeamName}`);

        //Subtitle
        svg
          .append("text")
          .attr("x", this.CHART_MARGIN.left * 0.66)
          .attr("y",(this.CHART_MARGIN.top * 0.75))
          .attr("text-anchor","start")
          .style("fill", this.isForPDF ? "#000" : "")
          .style("font-size",this.SVG_WIDTH * 0.016)
          .style("fill", this.darkMode && !this.isForPDF ? "white" : "rgba(0,0,0,0.69)")
          .text(`${this.game.leagueName} | ${this.game.localMatchDate ? this.game.localMatchDate : this.game.matchDate}`);

      },
      renderLogo(svg){
        // Draw Driblab Logo
        const logoWidth = this.SVG_WIDTH * 0.22;
        const logoHeight = this.SVG_HEIGHT * 0.1;
        let base_image = new Image();
        base_image.src = require(`@/assets/img/driblab_${this.darkMode ? 'white' : 'color'}.png`);
        base_image.onload = (event) => {
          const dataUrl = getDataUrl(event.currentTarget);
          let imgNode = svg.append("svg:image")
          .attr("x", this.SVG_WIDTH * 0.8125)
          .attr("y", this.SVG_HEIGHT - this.CHART_MARGIN.bottom/1.5)
          .attr("width", logoWidth * 0.8)
          .attr("height", logoHeight * 0.8)
          .attr("xlink:href", dataUrl);
        }
      }
    },
    destroyed() {
      delete this.observer;
    },
  }

</script>

<style lang="scss">
.axis-race-chart{
  .tick{
    text{
      font-size: 12px;
    }

  }
  &.y{
    line{
      stroke: lightgray;
      stroke-width: 2px;
      stroke-opacity: 0.5;
    }
  }
  &.x{
    text{
      font-size: 11px;
    }
  }
}


.grid line {
  stroke: lightgrey;
  stroke-opacity: 0.7;
  shape-rendering: crispEdges;
}

.grid path {
  stroke-width: 0;
}

</style>
